// First-person gloved hands and forearms. Hands are built per (side, hold) and parented under the
// weapon root, positioned on the weapon's grip anchors, so they move rigidly with the gun. The
// viewmodel animator may move `left` / `right` relative to the weapon (reload, equip).
//
// Hold styles come from `userData.hold` on the weapon's grip anchors (see weapons.ts):
//   "grip"   fist around a vertical grip (palm behind it, fingers wrap the front)
//   "cup"    support hand cupping a horizontal handguard from below
//   "handle" fist around the knife handle
//   "none"   hand hidden

import * as THREE from "three";
import { MeshBuilder } from "./geom";
import { ACCENT, CHAR } from "../materials/palette";
import type { WeaponModel } from "./weapons";

export interface ViewmodelHands {
  /** Parent of both hands; attachTo() adds it to the weapon root. */
  root: THREE.Group;
  left: THREE.Group;
  right: THREE.Group;
  /** Rest positions of the hands in weapon space after attachTo() (the grip anchors). */
  readonly leftRest: THREE.Vector3;
  readonly rightRest: THREE.Vector3;
  attachTo(weapon: WeaponModel): void;
  /**
   * Tucks the forearms in: 0 leaves them at full length, 1 (full ADS) shortens them to a stub so
   * the arms do not fill the lower corner of the view.
   */
  setTuck(t: number): void;
  /** Removes the hands from the weapon without disposing them. */
  detach(): void;
  dispose(): void;
}

export interface HandsOpts {
  /** Sleeve colour (default dark grey). */
  sleeve?: string;
  /** Cuff band colour (default runner red). */
  accent?: string;
}

type Hold = "grip" | "cup" | "handle";
type Side = 1 | -1;

const FOREARM_LEN = 0.43;
const FORWARD = new THREE.Vector3(0, 0, 1);

/** Closed fist around an object running along the local Y axis at the origin; palm behind (+Z). */
function fist(b: MeshBuilder, s: Side): void {
  const glove = CHAR.gloves;
  const pad = CHAR.glovesPad;
  b.box("main", [0.052, 0.088, 0.03], [0, 0, 0.034], { bevel: 0.01, color: glove });
  b.box("main", [0.046, 0.072, 0.008], [0, 0, 0.052], { color: pad });
  b.box("main", [0.014, 0.09, 0.03], [s * 0.047, 0, 0.03], { bevel: 0.004, color: pad });
  for (let i = 0; i < 4; i++) {
    const y = 0.033 - i * 0.022;
    b.box("main", [0.02, 0.02, 0.05], [s * 0.034, y, 0.012], { color: glove });
    b.box("main", [0.044, 0.02, 0.02], [s * 0.012, y, -0.02], { color: glove });
  }
  b.box("main", [0.02, 0.024, 0.045], [-s * 0.03, 0.034, 0.015], { color: glove });
  b.box("main", [0.018, 0.02, 0.035], [-s * 0.026, 0.034, -0.022], { rot: [0, s * 0.3, 0], color: glove });
}

/** A gloved hand and its forearm: the forearm is built along +Z from the wrist so it can be shortened. */
interface HandGeo {
  hand: THREE.BufferGeometry;
  arm: THREE.BufferGeometry;
  /** Wrist position and the rotation that turns +Z into the forearm direction (weapon space). */
  wrist: THREE.Vector3;
  armQuat: THREE.Quaternion;
}

function buildHand(side: Side, hold: Hold, sleeve: string, accent: string): HandGeo {
  const b = new MeshBuilder();
  // Wrist position and forearm direction in weapon space, per hold
  let wrist: THREE.Vector3;
  let dir: THREE.Vector3;
  if (hold === "grip") {
    b.at([0, 0, 0], () => fist(b, side));
    wrist = new THREE.Vector3(0, 0, 0.055);
    dir = new THREE.Vector3(side * 0.45, -0.45, 1);
  } else if (hold === "cup") {
    b.at([0, 0, 0], () => fist(b, side), [Math.PI / 2, 0, 0]);
    wrist = new THREE.Vector3(0, -0.052, 0);
    dir = new THREE.Vector3(side * 0.35, -0.45, 0.85);
  } else {
    b.at(
      [0, 0, 0],
      () => b.at([0, 0, 0], () => fist(b, side), [Math.PI / 2, 0, 0]),
      [0, 0, (side * Math.PI) / 2],
    );
    wrist = new THREE.Vector3(side * 0.052, 0, 0.02);
    dir = new THREE.Vector3(side * 0.35, -0.4, 0.85);
  }
  dir.normalize();
  const arm = new MeshBuilder();
  arm.box("main", [0.085, 0.085, FOREARM_LEN], [0, 0, 0.02 + FOREARM_LEN / 2], { bevel: 0.015, color: sleeve });
  arm.box("main", [0.095, 0.095, 0.05], [0, 0, 0.03], { bevel: 0.01, color: CHAR.strap });
  arm.box("main", [0.098, 0.098, 0.014], [0, 0, 0.068], { color: accent });
  return {
    hand: b.build({ vertexColors: true }).main,
    arm: arm.build({ vertexColors: true }).main,
    wrist,
    armQuat: new THREE.Quaternion().setFromUnitVectors(FORWARD, dir),
  };
}

export function createViewmodelHands(opts: HandsOpts = {}): ViewmodelHands {
  const sleeve = opts.sleeve ?? "#3a3d44";
  const accent = opts.accent ?? ACCENT.runnerRed;
  const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.05 });
  const geometries = new Map<string, HandGeo>();
  const geometryFor = (side: Side, hold: Hold): HandGeo => {
    const key = `${side}:${hold}`;
    let g = geometries.get(key);
    if (g === undefined) {
      g = buildHand(side, hold, sleeve, accent);
      geometries.set(key, g);
    }
    return g;
  };

  const root = new THREE.Group();
  root.name = "viewmodelHands";
  const left = new THREE.Group();
  left.name = "handLeft";
  const right = new THREE.Group();
  right.name = "handRight";
  const leftMesh = new THREE.Mesh(undefined, material);
  const rightMesh = new THREE.Mesh(undefined, material);
  // Forearms hang off a group at the wrist whose +Z runs along the arm, so scaling Z shortens it.
  const leftArm = new THREE.Group();
  const rightArm = new THREE.Group();
  const leftArmMesh = new THREE.Mesh(undefined, material);
  const rightArmMesh = new THREE.Mesh(undefined, material);
  leftArm.add(leftArmMesh);
  rightArm.add(rightArmMesh);
  left.add(leftMesh, leftArm);
  right.add(rightMesh, rightArm);
  root.add(left, right);
  left.visible = false;
  right.visible = false;

  const leftRest = new THREE.Vector3();
  const rightRest = new THREE.Vector3();

  const holdOf = (anchor: THREE.Object3D, fallback: Hold | "none"): Hold | "none" => {
    const h: unknown = anchor.userData.hold;
    return h === "grip" || h === "cup" || h === "handle" || h === "none" ? h : fallback;
  };

  return {
    root,
    left,
    right,
    leftRest,
    rightRest,
    attachTo(weapon: WeaponModel): void {
      weapon.root.add(root);
      const rightHold = holdOf(weapon.rightHandGrip, "grip");
      const leftHold = weapon.twoHanded ? holdOf(weapon.leftHandGrip, "cup") : "none";
      right.visible = rightHold !== "none";
      if (rightHold !== "none") {
        const g = geometryFor(1, rightHold);
        rightMesh.geometry = g.hand;
        rightArmMesh.geometry = g.arm;
        rightArm.position.copy(g.wrist);
        rightArm.quaternion.copy(g.armQuat);
      }
      right.position.copy(weapon.rightHandGrip.position);
      right.quaternion.copy(weapon.rightHandGrip.quaternion);
      left.visible = leftHold !== "none";
      if (leftHold !== "none") {
        const g = geometryFor(-1, leftHold);
        leftMesh.geometry = g.hand;
        leftArmMesh.geometry = g.arm;
        leftArm.position.copy(g.wrist);
        leftArm.quaternion.copy(g.armQuat);
      }
      left.position.copy(weapon.leftHandGrip.position);
      left.quaternion.copy(weapon.leftHandGrip.quaternion);
      rightRest.copy(right.position);
      leftRest.copy(left.position);
    },
    setTuck(t: number): void {
      const k = Math.min(Math.max(t, 0), 1);
      // The forearm shrinks to a fifth of its length and a little in girth.
      leftArm.scale.set(1 - 0.3 * k, 1 - 0.3 * k, 1 - 0.8 * k);
      rightArm.scale.set(1 - 0.3 * k, 1 - 0.3 * k, 1 - 0.8 * k);
    },
    detach(): void {
      root.removeFromParent();
    },
    dispose(): void {
      root.removeFromParent();
      for (const g of geometries.values()) {
        g.hand.dispose();
        g.arm.dispose();
      }
      geometries.clear();
      material.dispose();
    },
  };
}
